/**
 * Recuadro de firma con el dedo, compartido por todas las pantallas que
 * firman (LOPD, cuestionario de salud, consentimientos por enlace y en la
 * tablet, hoja de anestesia). Revisión de enlaces 2026-09-07.
 *
 * Por qué existe:
 *   - react-signature-canvas trae `clearOnResize=true` por defecto y en el
 *     móvil `resize` salta al abrir o cerrar el teclado (Android) y al
 *     plegarse la barra de direcciones (Chrome Android y Safari iOS); en la
 *     tablet, al girar o abrir el teclado. El paciente firmaba, tocaba un
 *     campo y la firma desaparecía sola. Aquí `clearOnResize` va a false.
 *   - Un lienzo con width/height fijos (760x260) reducido por CSS dibuja el
 *     trazo a escala hacia la esquina, lejos del dedo. Aquí el lienzo no lleva
 *     tamaño fijo: se ajusta al tamaño CSS con devicePixelRatio.
 *   - Si el tamaño CSS cambia de verdad (giro de la tablet), hay que ajustar
 *     el bitmap —si no, el trazo sale desplazado— y repintar lo firmado:
 *     guardamos los trazos en cada onEnd y los restauramos reescalados.
 *   - Safari descarta el bitmap de un canvas al volver de segundo plano: al
 *     volver a la pestaña se repinta desde los trazos guardados.
 *
 * API por ref (la misma que usaban las páginas con react-signature-canvas):
 * isEmpty(), clear(), getCanvas(), getTrimmedCanvas(), toDataURL().
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Eraser } from 'lucide-react';
import { Button } from './ui/button';

const copiaTrazos = (grupos) => grupos.map((g) => g.map((p) => ({ ...p })));

const FirmaDedo = forwardRef(function FirmaDedo(
  { testid = 'firma', alturaCls = 'h-48 sm:h-56', penColor = '#0B1220', derecha = null, disabled = false },
  ref,
) {
  const sigRef = useRef(null);
  // Último estado conocido: los trazos (toData) y el tamaño CSS del lienzo
  // cuando se dibujaron, para reescalarlos si el lienzo cambia de tamaño.
  const guardado = useRef(null);
  const [tieneTrazo, setTieneTrazo] = useState(false);

  const lienzo = () => sigRef.current?.getCanvas?.();
  const pad = () => sigRef.current?.getSignaturePad?.();

  const guardar = useCallback(() => {
    const c = lienzo();
    const p = pad();
    if (!c || !p) return;
    const datos = p.toData();
    // Copia: signature_pad sigue escribiendo en el array que devuelve toData.
    guardado.current = datos.length
      ? { trazos: copiaTrazos(datos), ancho: c.offsetWidth, alto: c.offsetHeight }
      : null;
    setTieneTrazo(!p.isEmpty());
  }, []);

  /**
   * Ajusta el bitmap al tamaño CSS actual si ha cambiado y repinta lo
   * guardado. Con `forzar` repinta aunque el tamaño sea el mismo (vuelta de
   * segundo plano). Si nada ha cambiado y la firma sigue ahí, no hace nada.
   */
  const restaurar = useCallback((forzar = false) => {
    const c = lienzo();
    const p = pad();
    if (!c || !p) return;
    const ancho = c.offsetWidth;
    const alto = c.offsetHeight;
    if (!ancho || !alto) return; // oculto: no hay a qué ajustarse
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    // canvas.width trunca a entero: comparar con lo que quedaría al asignar.
    const cambiaBitmap = c.width !== Math.floor(ancho * ratio) || c.height !== Math.floor(alto * ratio);
    if (cambiaBitmap) {
      // Asignar width/height borra el bitmap: solo cuando de verdad cambia.
      c.width = ancho * ratio;
      c.height = alto * ratio;
      c.getContext('2d').scale(ratio, ratio);
      p.clear();
    } else if (!forzar && !(guardado.current && p.isEmpty())) {
      return;
    }
    const g = guardado.current;
    if (!g) return;
    const fx = g.ancho ? ancho / g.ancho : 1;
    const fy = g.alto ? alto / g.alto : 1;
    p.fromData(g.trazos.map((grupo) => {
      const pts = grupo.map((pt) => ({ ...pt, x: pt.x * fx, y: pt.y * fy }));
      // signature_pad 2.3 no pinta nada con un trazo de exactamente dos
      // puntos (necesita tres para la curva) y lo daría por vacío: se repite
      // el último para que ese trazo corto salga como una raya y cuente.
      if (pts.length === 2) pts.push({ ...pts[1] });
      return pts;
    }));
    guardado.current = { trazos: copiaTrazos(p.toData()), ancho, alto };
    setTieneTrazo(!p.isEmpty());
  }, []);

  useEffect(() => {
    const alCambiar = () => restaurar(false);
    const alVolver = () => restaurar(true);
    const alVisible = () => { if (document.visibilityState === 'visible') restaurar(true); };
    window.addEventListener('resize', alCambiar);
    window.addEventListener('orientationchange', alCambiar);
    window.addEventListener('pageshow', alVolver);
    document.addEventListener('visibilitychange', alVisible);
    // El tamaño del lienzo puede cambiar sin `resize` (p. ej. al desplegarse
    // una sección encima): el observador lo ve igual.
    let observador = null;
    const c = lienzo();
    if (typeof ResizeObserver !== 'undefined' && c) {
      observador = new ResizeObserver(alCambiar);
      observador.observe(c);
    }
    return () => {
      window.removeEventListener('resize', alCambiar);
      window.removeEventListener('orientationchange', alCambiar);
      window.removeEventListener('pageshow', alVolver);
      document.removeEventListener('visibilitychange', alVisible);
      if (observador) observador.disconnect();
    };
  }, [restaurar]);

  const borrar = useCallback(() => {
    pad()?.clear();
    guardado.current = null;
    setTieneTrazo(false);
  }, []);

  useImperativeHandle(ref, () => ({
    isEmpty: () => { const p = pad(); return !p || p.isEmpty(); },
    clear: borrar,
    getCanvas: () => sigRef.current.getCanvas(),
    getTrimmedCanvas: () => sigRef.current.getTrimmedCanvas(),
    toDataURL: (tipo = 'image/png') => sigRef.current.getCanvas().toDataURL(tipo),
  }), [borrar]);

  return (
    <div data-testid={`${testid}-box`}>
      <div className="relative border-2 border-dashed border-[#2E6E5E]/60 rounded-md bg-white overflow-hidden">
        <SignatureCanvas
          ref={sigRef}
          penColor={penColor}
          clearOnResize={false}
          onEnd={guardar}
          canvasProps={{
            // Sin width/height fijos: react-signature-canvas ajusta el bitmap
            // al tamaño CSS con devicePixelRatio. `block` evita el hueco de
            // línea base bajo el canvas.
            className: `block w-full ${alturaCls}`,
            // Fondo SIEMPRE blanco (el PNG va a un PDF blanco) y sin gestos
            // del navegador (scroll, zoom) mientras se firma.
            style: { touchAction: 'none', backgroundColor: '#FFFFFF' },
            'data-testid': testid,
          }}
        />
        {!tieneTrazo && (
          <span
            className="pointer-events-none absolute left-2 top-2 text-xs text-muted-foreground select-none"
            data-testid={`${testid}-aviso`}
          >
            Firme dentro del recuadro
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <Button type="button" variant="ghost" size="sm" onClick={borrar} disabled={disabled} data-testid={`${testid}-clear`}>
          <Eraser className="w-4 h-4 mr-1" /> Borrar firma
        </Button>
        {derecha}
      </div>
    </div>
  );
});

export default FirmaDedo;
